import { HeartIcon } from '@phosphor-icons/react';
import { useId } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@shared/ui';

import styles from './Favorites.module.css';

/**
 * Избранное — закладка на заказе (BE-581, FE-586, DESIGN_SYSTEM §5.5).
 *
 * Отметка ничего не отправляет наружу и на подбор не влияет: ни на Match Score,
 * ни на отбор четвёрки (`selection_version` не меняется). Поэтому она
 * переключается одним нажатием, без подтверждения.
 *
 * Отмеченное сердечко — синее (`--color-accent`): это выбранное состояние
 * (DESIGN_SYSTEM §2.3). Красный в продукте означает главное действие, а не
 * «нравится».
 */

interface FavoriteToggleProps {
  pressed: boolean;
  onToggle: (next: boolean) => void;
  /** Марка, модель и модификация — чьё это сердечко, для скринридера. */
  vehicleLabel: string;
  busy?: boolean | undefined;
}

/**
 * Сердечко на фотографии карточки.
 *
 * Состояние несёт `aria-pressed`, а **имя кнопки не меняется** — иначе
 * скринридер говорил бы «Favorit entfernen, gedrückt», то есть называл бы
 * состояние дважды и противоречиво. Подсказка при наведении (`title`) при этом
 * называет действие, которое случится по нажатию. Чей это автомобиль, кнопка
 * сообщает описанием: двенадцать одинаковых «Als Favorit markieren» подряд
 * были бы неразличимы.
 */
export function FavoriteToggle({
  pressed,
  onToggle,
  vehicleLabel,
  busy = false,
}: FavoriteToggleProps) {
  const { t } = useTranslation();
  const descriptionId = useId();

  return (
    <>
      <button
        type="button"
        className={[styles.heart, pressed && styles.heartPressed].filter(Boolean).join(' ')}
        aria-pressed={pressed}
        aria-label={t('showcase.favorite.add')}
        aria-describedby={descriptionId}
        aria-busy={busy || undefined}
        title={pressed ? t('showcase.favorite.remove') : t('showcase.favorite.add')}
        onClick={() => {
          onToggle(!pressed);
        }}
      >
        <HeartIcon size={20} weight={pressed ? 'fill' : 'regular'} aria-hidden="true" />
      </button>
      <span id={descriptionId} hidden>
        {vehicleLabel}
      </span>
    </>
  );
}

/**
 * Сердечко внутри текста реплики Karl и пустого состояния.
 *
 * В макете здесь эмодзи ❤️, но эмодзи в интерфейсе запрещены (DESIGN_SYSTEM
 * §5.10): на его месте та же иконка, что на карточке, с текстовой подписью для
 * скринридера. Компонент без детей: `<Trans>` подставляет его на место
 * `<heart/>` в строке локали.
 */
export function InlineHeart() {
  const { t } = useTranslation();

  return (
    <span className={styles.inlineHeart} role="img" aria-label={t('showcase.favorite.heart')}>
      <HeartIcon size={16} weight="fill" aria-hidden="true" />
    </span>
  );
}

interface FavoritesFilterButtonProps {
  /** Отмеченные автомобили набора — `favorites_count` из ответа API. */
  count: number;
  active: boolean;
  onChange: (next: boolean) => void;
}

/**
 * «Meine Favoriten (n)» — переключатель `favorites_only` в адресе.
 *
 * Включённый — с синим тинтом и `aria-pressed`: цвет не единственный носитель
 * состояния. Как вернуться ко всем автомобилям, говорит ещё и строка над
 * сеткой (`FavoritesOnlyNote`) — у неё своя кнопка.
 */
export function FavoritesFilterButton({ count, active, onChange }: FavoritesFilterButtonProps) {
  const { t } = useTranslation();

  return (
    <Button
      variant="outline-action"
      className={[styles.filter, active && styles.filterActive].filter(Boolean).join(' ')}
      aria-pressed={active}
      onClick={() => {
        onChange(!active);
      }}
    >
      <span className={styles.filterLabel}>
        <HeartIcon size={16} weight={active ? 'fill' : 'regular'} aria-hidden="true" />
        <span className="tabular">{t('showcase.actions.favorites', { count })}</span>
      </span>
    </Button>
  );
}

/** Строка над сеткой, пока включено «Meine Favoriten»: что показано и как выйти. */
export function FavoritesOnlyNote({ onShowAll }: { onShowAll: () => void }) {
  const { t } = useTranslation();

  return (
    <div className={styles.note} role="status">
      <p className={styles.noteText}>
        <HeartIcon size={16} weight="fill" aria-hidden="true" />
        {t('showcase.favorites.only')}
      </p>
      <Button variant="ghost" size="sm" onClick={onShowAll}>
        {t('showcase.favorites.showAll')}
      </Button>
    </div>
  );
}

/**
 * «Meine Favoriten» без отметок (`empty_reason = NO_FAVORITES`).
 *
 * Отдельное пустое состояние, а не «ничего не найдено»: набор не пуст, просто
 * ни один автомобиль ещё не отмечен. Действие одно — показать все.
 */
export function FavoritesEmpty({ onShowAll }: { onShowAll: () => void }) {
  const { t } = useTranslation();
  const titleId = useId();

  return (
    <section className={styles.empty} aria-labelledby={titleId}>
      <h2 id={titleId} className={styles.emptyTitle}>
        {t('showcase.favorites.empty.title')}
      </h2>
      <p className={styles.emptyText}>
        <Trans i18nKey="showcase.favorites.empty.text" components={{ heart: <InlineHeart /> }} />
      </p>
      <Button variant="outline-action" onClick={onShowAll}>
        {t('showcase.favorites.showAll')}
      </Button>
    </section>
  );
}
